import { useState } from 'react';
import { hotComments } from '@/mocks/gossip';

const avatarColors = [
  'bg-primary-100 text-primary-700',
  'bg-accent-100 text-accent-700',
  'bg-secondary-100 text-secondary-700',
];

export default function FrontComments() {
  const [likes, setLikes] = useState<Record<string, number>>(
    Object.fromEntries(hotComments.map((c) => [c.id, c.likes])),
  );
  const [liked, setLiked] = useState<Record<string, boolean>>({});
  const [reactions, setReactions] = useState<string[]>([]);
  const [note, setNote] = useState('');
  const [draft, setDraft] = useState('');

  const toast = (msg: string) => {
    setNote(msg);
    window.setTimeout(() => setNote(''), 2200);
  };

  const toggleReaction = (r: string) => {
    setReactions((s) => (s.includes(r) ? s.filter((x) => x !== r) : [...s, r]));
  };

  const like = (id: string) => {
    setLiked((s) => {
      const on = !s[id];
      setLikes((l) => ({ ...l, [id]: l[id] + (on ? 1 : -1) }));
      return { ...s, [id]: on };
    });
  };

  return (
    <section id="comments" className="bg-background-100 py-12 md:py-20">
      <div className="mx-auto max-w-[1240px] px-4 md:px-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="flex items-center gap-2 text-sm font-bold text-primary-600">
              <i className="ri-chat-3-fill" />
              前排小板凳 · 互动与热评
            </p>
            <h2 className="mt-2 font-heading text-2xl font-black tracking-tight text-foreground-950 md:text-4xl">
              前排已经吵起来了，你站哪边
            </h2>
            <p className="mt-3 max-w-xl text-sm text-foreground-600 md:text-base">
              评论区比瓜本身还热闹。点赞、反驳、递内味，随手就能跟上节奏。
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {['赞同前排', '等反转', '有内味'].map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => toggleReaction(r)}
                className={`flex items-center gap-1.5 whitespace-nowrap rounded-full border px-4 py-2 text-xs font-bold transition ${
                  reactions.includes(r)
                    ? 'border-primary-500 bg-primary-500 text-background-50'
                    : 'border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300'
                }`}
              >
                {reactions.includes(r) ? (
                  <i className="ri-check-line" />
                ) : (
                  <i className="ri-emotion-happy-line" />
                )}
                {r}
              </button>
            ))}
          </div>
        </div>

        {/* 发言框 */}
        <div id="commentBox" className="mt-8 rounded-3xl border border-background-200 bg-background-50 p-5 md:p-6">
          <div className="flex gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-500 text-sm font-black text-background-50">
              我
            </span>
            <div className="flex-1">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value.slice(0, 200))}
                placeholder="前排就位，说说你对这口最新瓜的看法…（演示页，输入不会真的提交）"
                className="w-full resize-none rounded-2xl border border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition placeholder:text-foreground-400 focus:border-primary-400"
                rows={3}
              />
              <div className="mt-3 flex items-center justify-between">
                <span className="text-xs text-foreground-400">{draft.length}/200</span>
                <button
                  type="button"
                  onClick={() => {
                    if (!draft.trim()) {
                      toast('先写两句再发，前排等你的见解～');
                      return;
                    }
                    setDraft('');
                    toast('已占座！这是演示页面，评论没有真的提交～');
                  }}
                  className="flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-bold text-background-50 transition hover:bg-primary-600"
                >
                  <i className="ri-send-plane-fill" />
                  前排发言
                </button>
              </div>
            </div>
          </div>
          {note && (
            <p className="mt-3 flex items-center gap-1.5 rounded-xl bg-primary-50 px-4 py-2.5 text-xs font-medium text-primary-700">
              <i className="ri-information-line" />
              {note}
            </p>
          )}
        </div>

        {/* 评论流 */}
        <div className="mt-6 divide-y divide-background-200 rounded-3xl border border-background-200 bg-background-50 px-5 md:px-7">
          {hotComments.map((c, idx) => (
            <article key={c.id} className="flex flex-col gap-3 py-6">
              <div className="flex items-center gap-3">
                <span className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-black ${avatarColors[idx % 3]}`}>
                  {c.name.replace(/[·\s·]/g, '').slice(-2, -1) || c.name.slice(0, 1)}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold text-foreground-950">{c.name}</p>
                  <p className="mt-0.5 text-xs text-foreground-400">
                    {c.floor} · {c.time}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => like(c.id)}
                  aria-label="点赞"
                  className={`flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-bold transition ${
                    liked[c.id]
                      ? 'border-primary-500 bg-primary-500 text-background-50'
                      : 'border-background-200 bg-background-50 text-foreground-600 hover:border-primary-300'
                  }`}
                >
                  <i className={`${liked[c.id] ? 'ri-thumb-up-fill' : 'ri-thumb-up-line'} text-sm`} />
                  {likes[c.id]}
                </button>
              </div>
              <p className="pl-[52px] text-sm leading-relaxed text-foreground-800">{c.text}</p>
              <div className="flex items-center gap-5 pl-[52px] text-xs text-foreground-500">
                <button type="button" className="flex items-center gap-1 hover:text-primary-600" onClick={() => toast('仅演示，未接后端～')}>
                  <i className="ri-reply-line text-sm" />
                  {c.replies} 条回复
                </button>
                <button type="button" className="flex items-center gap-1 hover:text-primary-600" onClick={() => toast('已转发到前排（演示）')}>
                  <i className="ri-share-forward-line text-sm" />
                  转发
                </button>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-6 text-center">
          <a
            href="#speed"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-6 py-3 text-sm font-bold text-foreground-700 transition hover:border-primary-300 hover:text-primary-600"
          >
            <i className="ri-arrow-up-line" />
            回顶部看最新瓜
          </a>
        </div>
      </div>
    </section>
  );
}